import React from 'react';
import { motion } from 'framer-motion';
import { Toggle } from '../../ui/Toggle/Toggle';
import { AC_MODE_SPECS, POWER_STATE, TEMPERATURE_C } from '../../../lib/device/catalog';
import type { AcMode, PowerState } from '../../../lib/device/catalog';

export interface TemperatureControlProps {
  id: string;
  name: string;
  icon: React.ReactNode;
  status: PowerState;
  targetTemp: number;
  mode: AcMode;
  isOnline: boolean;
  onToggle: (id: string) => void;
  onTemperatureChange: (id: string, temp: number) => void;
  onModeChange: (id: string, mode: AcMode) => void;
  className?: string;
}

export const TemperatureControl: React.FC<TemperatureControlProps> = ({
  id,
  name,
  icon,
  status,
  targetTemp,
  mode,
  isOnline,
  onToggle,
  onTemperatureChange,
  onModeChange,
  className = '',
}) => {
  const isOn = status === POWER_STATE.ON;
  const { icon: ModeIcon, label: modeLabel, next: nextMode } = AC_MODE_SPECS[mode];

  const handleTempUp = () => {
    if (isOn && isOnline && targetTemp < TEMPERATURE_C.MAX) {
      onTemperatureChange(id, targetTemp + TEMPERATURE_C.STEP);
    }
  };

  const handleTempDown = () => {
    if (isOn && isOnline && targetTemp > TEMPERATURE_C.MIN) {
      onTemperatureChange(id, targetTemp - TEMPERATURE_C.STEP);
    }
  };

  const cycleMode = () => {
    if (!isOn || !isOnline) return;
    onModeChange(id, nextMode);
  };

  return (
    <motion.div
      className={`
        bg-[#F8F8F8] rounded-2xl p-4 
        ${!isOnline ? 'opacity-50' : ''}
        ${className}
      `}
      whileHover={isOnline ? { y: -2 } : undefined}
      transition={{ duration: 0.2 }}
    >
      {/* Top Row - Icon, Name, and Toggle */}
      <div className="flex items-center gap-4 mb-4">
        {/* Icon Container */}
        <div 
          className={`
            w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
            ${isOn ? 'bg-[#D4A054]' : 'bg-[#FDF6E9]'}
            transition-colors duration-300
          `}
        >
          <div className={`w-6 h-6 flex items-center justify-center ${isOn ? 'text-white' : 'text-[#D4A054]'}`}>
            {icon}
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-[#333333] text-base font-medium truncate">
            {name}
          </h3>
          <motion.p
            key={isOn ? 'on' : 'off'}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className={`text-sm ${isOn ? 'text-[#D4A054]' : 'text-[#666666]'}`}
          >
            {isOn ? `${targetTemp}°C` : 'Off'}
            {!isOnline && ' (Offline)'}
          </motion.p>
        </div>

        {/* Toggle Switch */}
        <Toggle
          checked={isOn}
          onChange={() => onToggle(id)}
          disabled={!isOnline}
        />
      </div>

      {/* Temperature Display and Controls */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handleTempDown}
          disabled={!isOn || !isOnline}
          className={`
            w-12 h-12 rounded-full flex items-center justify-center text-xl font-medium
            ${isOn && isOnline 
              ? 'bg-white text-[#D4A054] shadow-md hover:bg-[#FDF6E9]' 
              : 'bg-[#F0F0F0] text-[#CCCCCC] cursor-not-allowed'}
            transition-all duration-200
          `}
        >
          −
        </button>

        <div className="text-center">
          <div className={`
            text-4xl font-bold transition-colors duration-300
            ${isOn && isOnline ? 'text-[#D4A054]' : 'text-[#CCCCCC]'}
          `}>
            {targetTemp}°
          </div>
          <div className="text-xs text-[#999999] mt-1">Target</div>
        </div>

        <button
          onClick={handleTempUp}
          disabled={!isOn || !isOnline}
          className={`
            w-12 h-12 rounded-full flex items-center justify-center text-xl font-medium
            ${isOn && isOnline 
              ? 'bg-white text-[#D4A054] shadow-md hover:bg-[#FDF6E9]' 
              : 'bg-[#F0F0F0] text-[#CCCCCC] cursor-not-allowed'}
            transition-all duration-200
          `}
        >
          +
        </button>
      </div>

      {/* Mode Selector */}
      <button
        onClick={cycleMode}
        disabled={!isOn || !isOnline}
        className={`
          w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2
          ${isOn && isOnline 
            ? 'bg-white text-[#333333] shadow-sm hover:bg-[#FDF6E9]' 
            : 'bg-[#F0F0F0] text-[#999999] cursor-not-allowed'}
          transition-all duration-200
        `}
      >
        <ModeIcon className="w-5 h-5" />
        <span className="font-medium">{modeLabel}</span>
      </button>
    </motion.div>
  );
};

export default TemperatureControl;
